<template>
  <div class="min-h-screen bg-white dark:bg-black">
    <!-- Sidebar -->
    <Sidebar v-model:isOpen="isSidebarOpen" />
    
    <div class="lg:pl-[17rem]">
      <!-- Header -->
      <header
        class="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white dark:bg-black shadow"
      >
        <!-- Mobile Menu Button -->
        <button
          type="button"
          class="px-4 focus:outline-none focus:ring-2 focus:ring-inset lg:hidden"
          :style="{ color: brandColors.accent }"
          @click="toggleSidebar"
        >
          <span class="sr-only">Open sidebar</span>
          <svg
            class="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        <!-- Header Content -->
        <div class="flex flex-1 items-center justify-between px-4">
          <!-- Admin Brand Selector (admin mode only, hidden during customer preview) -->
          <div class="flex flex-1 items-center">
            <div
              v-if="currentUser?.role === 'admin' && !isInCustomerView"
              ref="brandSwitcherRef"
              class="relative"
            >
              <button
                type="button"
                class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 dark:border-gray-700 dark:bg-black dark:text-gray-200 dark:hover:bg-gray-900"
                :style="{ '--tw-ring-color': activeAdminBrand?.color || brandColors.primary }"
                @click="toggleBrandMenu"
              >
                <span class="sr-only">Select admin brand</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="h-5 w-5">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.75 6.75h14.5M4.75 12h14.5M4.75 17.25h14.5" />
                </svg>
              </button>

              <div
                v-if="isBrandMenuOpen"
                class="absolute left-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:border-gray-700 dark:bg-black"
              >
                <button
                  v-for="brand in availableBrands"
                  :key="brand.id"
                  type="button"
                  class="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  @click="handleAdminBrandSelect(brand.id)"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: brand.color }"
                    ></span>
                    <span>{{ brand.name }}</span>
                  </div>
                  <svg
                    v-if="authStore.adminBrandId === brand.id"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    class="h-4 w-4"
                    :style="{ color: brand.color }"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- TOTAL CONFIDENCE PLAN Text -->
          <div class="flex items-center mr-4">
            <p
              class="text-sm md:text-xl font-medium tracking-wider"
              :style="{ color: brandColors.primary }"
            >
              {{ currentBrand?.programName ?? 'TOTAL CONFIDENCE PLAN' }}
            </p>
          </div>

          <!-- Notification Dropdown (Admin Only, admin mode only) -->
          <NotificationDropdown v-if="currentUser?.role === 'admin' && !isInCustomerView" />

          <!-- User Profile Section -->
          <div class="flex items-center">
            <div class="relative ml-3">
              <!-- Profile Button -->
              <div>
                <button
                  type="button"
                  class="flex max-w-xs items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                  :style="{
                    backgroundColor: brandColors.accent,
                    '--tw-ring-color': brandColors.accent
                  }"
                  @click="toggleProfile"
                >
                  <span class="sr-only">Open user menu</span>
                  <div class="h-8 w-8 rounded-full flex items-center justify-center text-white">
                    {{ userInitials }}
                  </div>
                </button>
              </div>
              
              <!-- Profile Dropdown Menu -->
              <div
                v-if="isProfileOpen"
                class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-black py-1 shadow-lg ring-1 ring-black ring-opacity-5"
              >
                <!-- User Info -->
                <div class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                  <p class="font-medium truncate">{{ currentUser?.first_name }} {{ currentUser?.last_name }}</p>
                  <p class="text-gray-500 dark:text-gray-400 truncate">{{ currentUser?.email }}</p>
                  <p
                    class="text-xs mt-1 px-2 py-0.5 rounded-full inline-block text-white"
                    :style="{ backgroundColor: brandColors.accent }"
                  >
                    {{ isInCustomerView ? 'customer view' : currentUser?.role }}
                  </p>
                </div>
                <div class="border-t border-gray-100 dark:border-gray-700"></div>

                <!-- Theme Toggle -->
                <button
                  type="button"
                  class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="toggleTheme"
                >
                  <div class="flex items-center">
                    <span class="mr-3 text-lg">{{ isDarkMode ? '🌞' : '🌙' }}</span>
                    <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
                  </div>
                </button>

                <!-- Admin: Brand switch options -->
                <template v-if="currentUser?.role === 'admin'">
                  <div class="border-t border-gray-100 dark:border-gray-700"></div>

                  <!-- When in admin mode: show per-brand customer view options -->
                  <template v-if="!isInCustomerView">
                    <button
                      v-for="brand in availableBrands"
                      :key="brand.id"
                      type="button"
                      class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      @click="handleSwitchToCustomer(brand.id)"
                    >
                      <div class="flex items-center gap-2">
                        <span
                          class="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: brand.color }"
                        ></span>
                        <span>Customer View — {{ brand.name }}</span>
                      </div>
                    </button>
                  </template>

                  <!-- When in customer view: show back to admin -->
                  <button
                    v-else
                    type="button"
                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    @click="handleSwitchToAdmin"
                  >
                    <div class="flex items-center">
                      <svg class="mr-3 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>Back to Admin</span>
                    </div>
                  </button>

                  <div class="border-t border-gray-100 dark:border-gray-700"></div>
                </template>

                <!-- Logout Button -->
                <button
                  type="button"
                  class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  @click="handleLogout"
                  :disabled="isLoading"
                >
                  <div class="flex items-center">
                    <svg class="mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v3a1 1 0 102 0V9z" clip-rule="evenodd" />
                    </svg>
                    <span>{{ isLoading ? 'Logging out...' : 'Sign out' }}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- Customer View Mode Banner -->
      <div
        v-if="isInCustomerView"
        class="flex items-center justify-center gap-2 py-1.5 text-xs font-medium text-white"
        :style="{ backgroundColor: brandColors.primary }"
      >
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        You are viewing as a {{ activeBrandName }} Customer. Actions will be recorded under your admin account.
      </div>

      <!-- Main Content -->
      <main class="py-2 lg:py-6">
        <div class="w-full px-2 md:px-6 lg:px-6">
          <router-view></router-view>
        </div>
      </main>
    </div>
  </div>

  <!-- Complete Profile Modal -->
  <CompleteProfileModal
    :is-open="showCompleteProfileModal"
    @complete="goToCompleteProfile"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import { useBrand } from '@/composables/brand/useBrand'
import { BRANDS } from '@/config/brands'
import Sidebar from './Sidebar.vue'
import NotificationDropdown from './NotificationDropdown.vue'
import CompleteProfileModal from '@/components/customer/CompleteProfileModal.vue'

// Store Instances
const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
const { userInitials } = useUser()
const { handleLogout } = useLogin()
const { currentBrand, brandColors } = useBrand()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const isBrandMenuOpen = ref(false)
const showCompleteProfileModal = ref(false)
const brandSwitcherRef = ref<HTMLElement | null>(null)

// Computed Properties
const currentUser = computed(() => authStore.currentUser)
const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const isInCustomerView = computed(() => authStore.isInCustomerView)
const activeAdminBrand = computed(() => BRANDS.find(brand => brand.id === authStore.adminBrandId))

// All available brands for the switch menu
const availableBrands = computed(() => BRANDS)

// The name of the brand currently active in customer view
const activeBrandName = computed(() => {
  if (!authStore.adminViewBrandId) return ''
  return BRANDS.find(b => b.id === authStore.adminViewBrandId)?.name ?? ''
})

// Dynamic Styles
const headerStyle = computed(() => ({
  backgroundColor: '#ffffff',
  borderBottomWidth: '1px',
  borderBottomColor: brandColors.value.accent + '20',
}))

// Methods
const toggleProfile = () => {
  isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleBrandMenu = () => {
  isBrandMenuOpen.value = !isBrandMenuOpen.value
}

const toggleTheme = () => {
  themeStore.toggleTheme()
}

const handleAdminBrandSelect = (brandId: number) => {
  authStore.setAdminBrand(brandId)
  isBrandMenuOpen.value = false
}

const handleSwitchToCustomer = (brandId: number) => {
  isProfileOpen.value = false
  authStore.switchToCustomerView(brandId)
  router.push({ name: 'tcp' })
}

const handleSwitchToAdmin = () => {
  isProfileOpen.value = false
  authStore.switchToAdminView()
  router.push({ name: 'admin-dashboard' })
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

const handleBrandMenuOutsideClick = (event: MouseEvent) => {
  const target = event.target as Node
  if (brandSwitcherRef.value && !brandSwitcherRef.value.contains(target)) {
    isBrandMenuOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('click', handleBrandMenuOutsideClick)

  // Check if customer profile is incomplete
  const user = authStore.currentUser
  if (user && user.role === 'customer' && (!user.mobile || !user.city)) {
    showCompleteProfileModal.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('click', handleBrandMenuOutsideClick)
})

const goToCompleteProfile = () => {
  showCompleteProfileModal.value = false
  router.push({ name: 'customer-info' })
}
</script>
